/**
 * Chat stage derivation (logging / future UX). Sticky bottom CTA bar removed — all choices are bubble quick-replies.
 */

/** @typedef {"opening"|"search_results"|"package_selected"|"room_selected"|"passenger_form"|"baggage"|"payment"|"post_passengers"|"conversation"|"completed"} ChatStage */

export const CHAT_STAGE = {
  OPENING: "opening",
  SEARCH_RESULTS: "search_results",
  PACKAGE_SELECTED: "package_selected",
  ROOM_SELECTED: "room_selected",
  PASSENGER_FORM: "passenger_form",
  BAGGAGE: "baggage",
  PAYMENT: "payment",
  POST_PASSENGERS: "post_passengers",
  CONVERSATION: "conversation",
  COMPLETED: "completed",
};

function _qrText(qr) {
  return `${String(qr?.label ?? "")} ${String(qr?.value ?? "")}`;
}

function _lastAssistant(messages) {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m?.role === "assistant" && !m.searchLoading) {
      return { index: i, m };
    }
  }
  return null;
}

/**
 * @param {unknown[]} messages
 * @returns {{ stage: ChatStage, sourceMessageIndex: number, sourceMessage: object|null }}
 */
export function deriveChatStage(messages) {
  if (!Array.isArray(messages) || messages.length === 0) {
    return { stage: CHAT_STAGE.OPENING, sourceMessageIndex: -1, sourceMessage: null };
  }
  const last = _lastAssistant(messages);
  if (!last) {
    return { stage: CHAT_STAGE.OPENING, sourceMessageIndex: -1, sourceMessage: null };
  }
  const { m, index } = last;

  if (m.passenger_form?.passengers?.length > 0) {
    return { stage: CHAT_STAGE.PASSENGER_FORM, sourceMessageIndex: index, sourceMessage: m };
  }
  if (m.package_details_view) {
    return { stage: CHAT_STAGE.PACKAGE_SELECTED, sourceMessageIndex: index, sourceMessage: m };
  }
  if (Array.isArray(m.cards) && m.cards.length > 0) {
    return { stage: CHAT_STAGE.SEARCH_RESULTS, sourceMessageIndex: index, sourceMessage: m };
  }

  const qrs = m.quick_replies;
  if (Array.isArray(qrs) && qrs.length > 0) {
    if (qrs.some((qr) => /כבודה|מזוודה|baggage/i.test(_qrText(qr)))) {
      return { stage: CHAT_STAGE.BAGGAGE, sourceMessageIndex: index, sourceMessage: m };
    }
    if (qrs.some((qr) => /תשלום|קישור|payment|pay/i.test(_qrText(qr)))) {
      return { stage: CHAT_STAGE.PAYMENT, sourceMessageIndex: index, sourceMessage: m };
    }
    return { stage: CHAT_STAGE.POST_PASSENGERS, sourceMessageIndex: index, sourceMessage: m };
  }

  return { stage: CHAT_STAGE.CONVERSATION, sourceMessageIndex: index, sourceMessage: m };
}

/**
 * Legacy hook: sticky composer CTA bar disabled — returns none (quick replies stay in bubbles).
 * @returns {{ kind: "none", label: string, sourceMessageIndex: number, traceId?: string }}
 */
export function resolveChatBottomCta(messages, stageInfo) {
  const { sourceMessageIndex, sourceMessage: m } = stageInfo;
  if (!m || sourceMessageIndex < 0) {
    return { kind: "none", label: "", sourceMessageIndex: -1 };
  }
  return { kind: "none", label: "", sourceMessageIndex, traceId: m.trace_id };
}

/**
 * @param {unknown[]} messages
 * @returns {ReturnType<typeof resolveChatBottomCta>}
 */
export function computeChatBottomCta(messages) {
  const stageInfo = deriveChatStage(messages);
  return resolveChatBottomCta(messages, stageInfo);
}

/**
 * While a turn is in flight, the last assistant bubble may still be the pre-reply one
 * (e.g. date-range quick replies). Hide sticky CTAs until the new assistant message arrives.
 * @param {{ loading?: boolean, bootLoading?: boolean }} opts
 */
export function resolveChatBottomCtaWhenReady(messages, stageInfo, opts = {}) {
  const loading = Boolean(opts.loading);
  const bootLoading = Boolean(opts.bootLoading);
  if (loading && !bootLoading) {
    return { kind: "none", label: "", sourceMessageIndex: -1 };
  }
  return resolveChatBottomCta(messages, stageInfo);
}
